import React, { useState, useEffect } from "react";
import { CURRENCY } from "../constants";
import {
  useAirtelDepositMutation,
  useDepositMutation,
  useMpesaDespositMutation,
} from "../store/apis";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, updateUser } from "../store/auth-slice";
import { useSearchParams } from "react-router-dom";
import { handleApiError } from "../errors";
import { detectMobileCarrier, detectNetworkProvider } from "../utils";
import { toastService } from "../screens/notificationPage/ToastContainer";

export const DepositModal = ({ onClose }) => {
  const [deposit, { isLoading }] = useDepositMutation();
  const [depositAmount, setDepositAmount] = useState();
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [mpesaPush, { isLoading: isMpesaLoading }] = useMpesaDespositMutation();
  const [airtelPush, { isLoading: isAirtelLoading }] =
    useAirtelDepositMutation();

  // Detect user's network provider
  const [networkProvider, setNetworkProvider] = useState("Unknown");

  // Quick amount options
  const quickAmounts = [100, 200, 300, 400, 500, 1000, 2000, 5000];

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("mpesa"); // 'mpesa' or 'airtel'

  // Set if Airtel is available
  const isAirtelAvailable = false; // Set this to false for production since Airtel is not yet implemented

  // Detect network provider on component mount
  useEffect(() => {
    if (user && user.username) {
      const provider = detectNetworkProvider(user.username);
      setNetworkProvider(provider);

      // Set the payment method based on user's network
      if (provider === "Safaricom") {
        setPaymentMethod("mpesa");
      } else if (provider === "Airtel") {
        setPaymentMethod("airtel");
      } else {
        // Default to M-Pesa for unknown networks
        setPaymentMethod("mpesa");
      }
    }
  }, [user]);

  const handleDeposit = async () => {
    if (isNaN(depositAmount) || depositAmount <= 0) {
      toastService.error("Please enter a valid deposit amount");
      return;
    }

    try {
      let response;
      if (paymentMethod === "mpesa") {
        response = await mpesaPush({
          username: user.username,
          amount: depositAmount,
        }).unwrap();
        toastService.success(
          response.msg || "M-Pesa deposit request sent successfully"
        );
      } else if (paymentMethod === "airtel" && isAirtelAvailable) {
        response = await airtelPush({
          msisdn: user.username,
          amount: depositAmount,
          reference: "TEST",
        }).unwrap();
        toastService.success(
          response.msg || "Airtel Money deposit request sent successfully"
        );
      }

      setDepositAmount(0);
      onClose();
    } catch (error) {
      handleApiError(error, {
        showToast: true,
        toastPosition: "top-right",
      });
    }
  };

  const handleQuickAmount = (amount) => {
    setDepositAmount(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-bluedarkmode rounded-2xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold dark:text-white">
            Deposit Funds
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white text-xl sm:text-2xl p-1"
          >
            ✕
          </button>
        </div>

        {/* Payment Method Selector */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm sm:text-base">
            Payment Method
          </label>
          <div className="flex space-x-2">
            {networkProvider === "Safaricom" && (
              <button
                onClick={() => setPaymentMethod("mpesa")}
                className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg border text-sm sm:text-base transition-colors ${
                  paymentMethod === "mpesa"
                    ? "bg-green-100 border-green-500 dark:bg-green-900 dark:border-green-700"
                    : "bg-gray-100 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-green-600 dark:text-green-300">
                    M-Pesa
                  </span>
                </div>
              </button>
            )}
            {networkProvider === "Airtel" && (
              <button
                onClick={() => isAirtelAvailable && setPaymentMethod("airtel")}
                disabled={!isAirtelAvailable}
                className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-lg border text-sm sm:text-base transition-colors ${
                  paymentMethod === "airtel"
                    ? "bg-red-100 border-red-500 dark:bg-red-900 dark:border-red-700"
                    : "bg-gray-100 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                } ${!isAirtelAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-red-600 dark:text-red-300">
                    Airtel Money{!isAirtelAvailable && " (Coming Soon)"}
                  </span>
                </div>
              </button>
            )}
          </div>
          {networkProvider === "Airtel" && !isAirtelAvailable && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Airtel Money payments are coming soon. Please use M-Pesa for now.
            </p>
          )}
        </div>

        {/* Amount Input */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm sm:text-base">
            Deposit Amount
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-yellow text-darkblue px-2 py-1 rounded text-xs sm:text-sm font-bold">
              {CURRENCY}
            </div>
            <input
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(Number(e.target.value))}
              className="w-full p-3 sm:p-4 pl-14 sm:pl-16 border rounded-lg dark:bg-darktitleColor dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              placeholder="0.00"
              min="0"
            />
          </div>
        </div>

        {/* Quick Amount Buttons */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-gray-700 dark:text-gray-300 mb-2 text-sm sm:text-base">
            Quick Amounts
          </label>
          <div className="grid grid-cols-4 gap-1 sm:gap-2">
            {quickAmounts.map((amount) => (
              <button
                key={amount}
                onClick={() => handleQuickAmount(amount)}
                className={`py-2 sm:py-3 px-2 sm:px-3 rounded-lg border text-xs sm:text-sm font-medium text-yellow transition-colors ${
                  depositAmount === amount
                    ? "bg-yellow border-blue-500 dark:bg-darktitleColor dark:border-blue-700"
                    : "bg-yellow border-gray-300 dark:bg-darktitleColor dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
              >
                {CURRENCY}
                {amount}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-darktitleColor dark:hover:bg-gray-600 text-gray-800 dark:text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={handleDeposit}
            disabled={
              isLoading || isMpesaLoading || isAirtelLoading || !depositAmount
            }
            className={`flex-1 bg-yellow hover:bg-yellow-dark dark:bg-yellow dark:hover:bg-yellow-dark text-darkblue py-3 px-4 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base ${
              isLoading || isMpesaLoading || isAirtelLoading || !depositAmount
                ? "opacity-70 cursor-not-allowed"
                : ""
            }`}
          >
            {isLoading || isMpesaLoading || isAirtelLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-darkblue"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              `Deposit ${CURRENCY}${depositAmount ?? 0}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
